import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styles from "./TabPanel.module.css";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { Trans } from "@coworker/locales";
import { LoadMore } from "../../../shared/components/LoadMore/LoadMore";
import { useItemsInfoQueries } from "../../../shared/hooks/item/useItemsInfo";
import { Item } from "../../../shared/types/item";
import { FullTask } from "../../types/FullTask";
import { useTeamId, useUID } from "@coworker/app/src/core/auth/useLoggedInUser";
import StackIconPath from "../../../shared/wrappers/FixaSSRIcon/paths/StackIconPath";
import ArrowLeftIconPath from "../../../shared/wrappers/FixaSSRIcon/paths/ArrowLeftIconPath";
import { ModalContext } from "../../../layout/context/ModalContextProvider";
import { ModalType } from "../../../layout/context/modalType";
import trackerHelper from "@coworker/app/src/helpers/tracker";
import { TaskType } from "@coworker/apprestructured/src/tasks/enums/taskTypes";
import {
  TaskListItem,
  useTasksOverview,
} from "@coworker/apprestructured/src/TasksOverview/hooks/useTasksOverview";
import { SwipeableTaskCard } from "@coworker/apprestructured/src/tasks/components/TaskCard/SwipeableTaskCard";
import { shouldDisplayPickupTask } from "@coworker/app/src/components/TaskDetail/actions/helpers";
import TaskCard from "@coworker/apprestructured/src/task-management/components/TaskCard/TaskCard";
import ListableTaskCardWrapper from "@coworker/apprestructured/src/task-management/components/TaskCard/wrappers/NormalTaskWrapper/ListableTaskCardWrapper";
import useSearchParamState from "@coworker/apprestructured/src/shared/hooks/useSearchParamState";
import GroupTitle from "@coworker/apprestructured/src/TasksOverview/components/GroupTitle/GroupTitle";
import { TaskCardSkeleton } from "@coworker/apprestructured/src/tasks/components/TaskCard/TaskCardSkeleton";
import { FixaText } from "@coworker/apprestructured/src/shared/wrappers/FixaText/FixaText";
import { MULTI_SELECT_TASK_TABS, TasksTabs } from "../../constants/tasksTabs";
import { MultiSelectPanel } from "../MultiSelect/MultiSelectPanel";
import { mapFilterValuesToObject } from "../../helpers/mapFilterValuesToObject";
import { DEFAULT_FILTER_VALUES } from "../../constants/defaultFilterValues";
import { getAppliedFiltersCount } from "../../helpers/getAppliedFiltersCount";
import { useTranslation } from "@coworker/locales";
import { TaskTabFilters } from "../../hooks/useTasksOverviewTabs";

const DESKTOP_SCROLL_CONTAINER_MEDIA_QUERY = "(min-width: 1200px)";

type TaskOrHeaderListItem =
  TaskListItem | { type: "group-title"; locationCode: string };

interface TabPanelProps {
  tab: TasksTabs;
  filters: TaskTabFilters;
  onShowTaskDetails: (taskId: string, taskType: TaskType) => void;
}

export const TabPanel = ({
  tab,
  filters,
  onShowTaskDetails,
}: TabPanelProps) => {
  const { t } = useTranslation();

  const filtersValues = useMemo(() => {
    return {
      ...DEFAULT_FILTER_VALUES,
      ...mapFilterValuesToObject(filters.filterValues),
    };
  }, [filters.filterValues]);

  const tasksQueryResult = useTasksOverview(filtersValues, tab);

  const tasks = useMemo(() => {
    return tasksQueryResult?.data?.pages.map((page) => page.list).flat() || [];
  }, [tasksQueryResult?.data?.pages]);

  const filteredCount = tasksQueryResult?.data?.pages[0]?.filteredCount || 0;
  const totalCount = tasksQueryResult?.data?.pages[0]?.totalCount || 0;
  const isLoading = tasksQueryResult?.isLoading || false;
  const isFetching = tasksQueryResult?.isFetching || false;
  const hasNextPage = tasksQueryResult?.hasNextPage ?? false;

  const itemNos = useMemo(() => {
    return tasks
      .filter(({ type }) => type === "v1")
      .map(({ task_data: task }) => (task as FullTask).product_article_id)
      .filter(Boolean);
  }, [tasks]);

  const itemsInfoResult = useItemsInfoQueries(itemNos);

  const itemsInfo = useMemo(() => {
    return itemsInfoResult.map((result) => result.data).filter(Boolean);
  }, [itemsInfoResult]);

  const uid = useUID();
  const { toggleModal } = React.useContext(ModalContext);
  const teamId = useTeamId();
  const [, setSearchParams] = useSearchParamState();
  const tabPanelRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const topSentinelRef = useRef<HTMLDivElement>(null);
  const [showScrollTopButton, setShowScrollTopButton] = useState(false);

  const appliedFiltersCount = useMemo(() => {
    return getAppliedFiltersCount(filtersValues);
  }, [filtersValues]);

  const groupBy = filtersValues.groupBy ?? "";

  const handleTaskV2CardClick = useCallback(
    (taskId: string) => {
      setSearchParams((params) => {
        params.set("taskViewer", String(taskId));
      });
    },
    [setSearchParams]
  );

  const locationGroups = useMemo(() => {
    return tasksQueryResult?.data?.pages[0]?.locationGroups || [];
  }, [tasksQueryResult?.data?.pages]);

  useEffect(() => {
    const sentinel = topSentinelRef.current;

    if (!sentinel) {
      return;
    }

    const mediaQuery = window.matchMedia(DESKTOP_SCROLL_CONTAINER_MEDIA_QUERY);
    let observer: IntersectionObserver | null = null;

    const observeSentinel = () => {
      observer?.disconnect();

      const root = mediaQuery.matches
        ? wrapperRef.current
        : tabPanelRef.current;

      if (!root) {
        return;
      }

      observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];

          if (!entry) {
            return;
          }

          setShowScrollTopButton(!entry.isIntersecting);
        },
        {
          root,
          threshold: 0,
          rootMargin: "0px",
        }
      );

      observer.observe(sentinel);
    };

    observeSentinel();
    mediaQuery.addEventListener("change", observeSentinel);

    return () => {
      observer?.disconnect();
      mediaQuery.removeEventListener("change", observeSentinel);
    };
  }, []);

  const handleScrollTopClick = useCallback(() => {
    const scrollElement = window.matchMedia(
      DESKTOP_SCROLL_CONTAINER_MEDIA_QUERY
    ).matches
      ? wrapperRef.current
      : tabPanelRef.current;

    scrollElement?.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const tasksAndHeaders: TaskOrHeaderListItem[] = useMemo(() => {
    if (!groupBy || !locationGroups.length) {
      return tasks;
    }

    const result: TaskOrHeaderListItem[] = [];

    locationGroups.forEach((group) => {
      result.push({ type: "group-title", locationCode: group.locationCode });

      tasks
        .filter((task) => task.locationCode === group.locationCode)
        .forEach((task) => result.push(task));
    });

    return result;
  }, [groupBy, locationGroups, tasks]);

  const showLoadMore = hasNextPage && !isLoading && !isFetching;
  const showNoResults = !isLoading && !isFetching && !tasks.length;

  return (
    <div ref={tabPanelRef} className={styles["tabPanel"]}>
      <div className={styles["title"]}>
        {!isLoading && (
          <>
            {MULTI_SELECT_TASK_TABS.includes(tab) && (
              <FixaButton
                className={styles["multiSelectButton"]}
                iconOnly
                ssrIcon={StackIconPath}
                size="xsmall"
                type="tertiary"
                onClick={() => {
                  trackerHelper.trackMultiSelectButtonTab(tab);
                  toggleModal({
                    type: ModalType.MultiSelectTasks,
                    payload: {
                      type: tab,
                    },
                  });
                }}
              />
            )}
            <div className={styles["counts"]}>
              {filteredCount < totalCount && (
                <FixaText textStyle="Heading.XS">
                  {`${filteredCount}/${totalCount} ${t("TasksString")}`}
                </FixaText>
              )}
              {filteredCount === totalCount && (
                <FixaText textStyle="Heading.XS">
                  {`${totalCount} ${totalCount === 1 ? t("TaskString") : t("TasksString")}`}
                </FixaText>
              )}
            </div>
          </>
        )}
      </div>
      <div ref={wrapperRef} className={styles["wrapper"]}>
        <div
          ref={topSentinelRef}
          className={styles["topSentinel"]}
          aria-hidden="true"
        />
        {!isLoading &&
          tasksAndHeaders.length > 0 &&
          tasksAndHeaders.map((taskOrHeaderListItem) => {
            const { type } = taskOrHeaderListItem;
            switch (type) {
              case "v1": {
                const taskListItem = taskOrHeaderListItem as TaskListItem & {
                  type: "v1";
                };
                const task = taskListItem.task_data;
                return (
                  <SwipeableTaskCard
                    key={`v1_${task.id}`}
                    task={task}
                    item={
                      itemsInfo?.find(
                        (item) => item?.no === task.product_article_id
                      ) as Item
                    }
                    multiSelect={false}
                    isMyTasks={task.assigned_user_id === uid}
                    type={tab}
                    disableSwipe={
                      task.state === "COMPLETED" || task.state === "CLOSED"
                    }
                    disablePickup={
                      !shouldDisplayPickupTask(task, {
                        uid: "",
                        team_id: teamId,
                        role_id: "",
                      })
                    }
                    onShowTaskDetails={onShowTaskDetails}
                  />
                );
              }
              case "v2": {
                const taskListItem = taskOrHeaderListItem as TaskListItem & {
                  type: "v2";
                };
                return (
                  <ListableTaskCardWrapper
                    key={`v2_${taskListItem.task_data.id}`}
                    onSelect={() =>
                      handleTaskV2CardClick(taskListItem.task_data.id)
                    }
                  >
                    <TaskCard task={taskListItem.task_data} />
                  </ListableTaskCardWrapper>
                );
              }
              case "group-title": {
                const groupItem = taskOrHeaderListItem as {
                  type: "group-title";
                  locationCode: string;
                };
                return (
                  <GroupTitle
                    key={`loc_${groupItem.locationCode}`}
                    locationCode={groupItem.locationCode}
                  />
                );
              }
            }
          })}
        {showNoResults && (
          <div className={styles["noResults"]}>
            <FixaText textStyle="Heading.M">
              {t("noTaskResultsString")}
            </FixaText>
            {!tasks.length && appliedFiltersCount > 0 ? (
              <>
                <FixaText textStyle="Body.M">
                  {t("noTasksWithAppliedFiltersString")}
                </FixaText>
                <FixaButton
                  onClick={filters.resetFilters}
                  text={<Trans>clearFiltersString</Trans>}
                  size="small"
                  type="secondary"
                  className={styles["clearFiltersButton"]}
                />
              </>
            ) : (
              <FixaText textStyle="Body.M">
                {t("noTasksAvailableString")}
              </FixaText>
            )}
          </div>
        )}
        {(isLoading || isFetching) && (
          <div className={styles["skeleton"]}>
            <TaskCardSkeleton />
            <TaskCardSkeleton />
            <TaskCardSkeleton />
            <TaskCardSkeleton />
          </div>
        )}
        {showLoadMore && (
          <LoadMore
            onLoadMoreClick={tasksQueryResult?.fetchNextPage}
            loadedItemsCount={tasks.length}
            totalCount={filteredCount}
            isFetching={isFetching}
          />
        )}
        {showScrollTopButton && (
          <FixaButton
            iconOnly
            ssrIcon={ArrowLeftIconPath}
            size="small"
            type="emphasised"
            className={styles["scrollTopButton"]}
            onClick={handleScrollTopClick}
          />
        )}
      </div>
      <MultiSelectPanel
        filters={filtersValues}
        onShowTaskDetails={onShowTaskDetails}
        title={
          tab === TasksTabs.OPEN
            ? t("openTasksString")
            : tab === TasksTabs.IN_PROGRESS
              ? t("inProgressTasksString")
              : tab === TasksTabs.COMPLETED
                ? t("completd3String")
                : tab === TasksTabs.CLOSED
                  ? t("teamClosedString")
                  : ""
        }
      />
    </div>
  );
};
